import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CreateGoalPayload, UpdateGoalPayload } from 'types/payloads/GoalPayload';
import { Goal } from 'types/Goal';

import CustomConfirmDialog from 'components/_common/ConfirmDialog';

import { IconSvg } from 'components/_new/Icon';
import { Button } from 'components/_new/Button';
import GoalForm from './GoalForm';
import GoalText from './GoalText';

interface Props {
	goalData: Goal;
	index: number;
	editedGoal: number | null;
	getGoals: () => void;
	updateGoal: (goalValue: UpdateGoalPayload) => void;
	saveNewGoal: (goalValue: CreateGoalPayload) => void;
	deleteGoal: (deletedGoalId: number) => void;
	toggleGoalStep: (payload: { goalId: number; stepId: number }) => void;
	setEditedGoal: (id: number | null) => void;
	bold?: boolean;
}

const GoalItem = ({
	goalData,
	index,
	editedGoal,
	getGoals,
	saveNewGoal,
	updateGoal,
	deleteGoal,
	toggleGoalStep,
	setEditedGoal,
	bold = false,
}: Props) => {
	const { t } = useTranslation();
	const [editPopup, setEditPopup] = useState(false);

	const onEditClick = () => {
		getGoals();
		setEditedGoal(goalData.id);
	};

	const deleteGoalConfirm = () => {
		deleteGoal(goalData.id);
		// setEditedGoal(null) jest teraz wywoływane w useGoals onMutationSuccess
	};

	const isEdited = editedGoal === goalData.id;

	const cancelEdit = () => {
		setEditedGoal(null);
		getGoals();
	};

	const goalFormId = `goal-form-${goalData.id ? goalData.id : `idx-${index}`}`;
	const onGoalStepToggle = (stepId: number) => {
		toggleGoalStep({ goalId: goalData.id, stepId });
	};

	return (
		<div
			className="flex flex-column gap-2 b-bottom pl-4 pr-4 pt-2 pb-4 border-round-lg"
			style={{ background: 'rgb(252,251,255)', boxShadow: ' 6px 6px 12px #c4c1d7, -6px -6px 12px #ffffff' }}
		>
			<div
				className="flex flex-row gap-2 w-full justify-content-between align-items-center pb-2"
				style={{ borderBottom: '1px solid lightGray' }}
			>
				<div className="flex flex-row gap-2 align-items-center">
					<span className="ml-2 flex-shrink-0 goal-num">{`${index}`}</span>
					<h3>{goalData.goal}</h3>
				</div>
				{editedGoal !== goalData.id && (
					<div className="flex flex-row gap-2 align-items-center">
						<Button
							label={t('actions.edit')}
							iconOnly
							icon={() => <IconSvg name="file-edit" />}
							iconClassName="icon-container icon-container-sm text-purple-500"
							onClick={onEditClick}
							variant="transparent"
							variantSize="sm"
						/>

						<Button
							label={t('actions.remove')}
							iconOnly
							icon={() => <IconSvg name="trash" />}
							iconClassName="icon-container icon-container-sm text-pink-500"
							onClick={() => setEditPopup(true)}
							variant="transparent"
							variantSize="sm"
						/>
					</div>
				)}
			</div>
			{isEdited ? (
				<GoalForm
					id={goalFormId}
					editedGoal={editedGoal}
					goalData={goalData}
					saveNewGoal={saveNewGoal}
					updateGoal={updateGoal}
					getGoals={getGoals}
				/>
			) : (
				<GoalText goalData={goalData} bold={bold} onGoalStepToggle={onGoalStepToggle} />
			)}
			{editedGoal === goalData.id && (
				<div className="flex items-center justify-content-end gap-2">
					<Button submit form={goalFormId} label={t('actions.save')} variant="primary" />
					<Button label={t('actions.cancel')} onClick={cancelEdit} variant="primary-outlined" />
				</div>
			)}
			<CustomConfirmDialog
				confirmationOpen={editPopup}
				setConfirmationOpen={() => setEditPopup(false)}
				message={t('userPanel.goal.removeGoalConfirm')}
				handleAccept={deleteGoalConfirm}
			/>
		</div>
	);
};

export default GoalItem;
